import React from "react";
import { observer } from "mobx-react-lite";
import { Box, Text, HStack, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import DraggableTable from "../../components/DraggableTable";
import EmptyState from "../../components/EmptyState/EmptyState";
import { AlertDelete } from "../../components/AlertDelete/AlertDelete";
import { SCREEN_VERTICAL_SPACING } from "../../constants";
import { LocationCreateMenu } from "./components/LocationCreateMenu";
import { LocationDrawer } from "./components/LocationDrawer";
import { LocationSearchFilter } from "./components/LocationSearchFilter";
import { useLocationPage } from "./hooks";
import { StoreState } from "enum/StoreState";
import { CoordinatesType } from "./types";
import { EditStationModal } from "pages/Admin/ManageStation/Components/EditStationModal";
import noLocationsImage from "../../assets/noLocations.svg";

const LocationPage: React.FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "locations",
  });

  const {
    locationStore,
    initialValues,
    validationSchema,
    handleCloseDrawer,
    handleSubmit,
    isOpen,
    isModalOpen,
    editStation,
    handleCloseModal,
    isEditing,
    isLoading,
    handleDrawingMode,
    handleGeoJsonLoaded,
    clearGeofence,
    selectedGeofence,
    setIsGeofenceLoadedByGeoJson,
    searchTerm,
    setSearchTerm,
    filteredLocations,
    columns,
    handleCreateLocation,
    handleEdit,
    handleDelete,
    isDeleteOpen,
    onDeleteClose,
    handleConfirmDelete,
    enablePagination,
    currentPage,
    pageSize,
    totalItems,
    handlePageChange,
    handleSaveEditStation,
    isStationLoading,
    isGeofenceInvalid,
    canDeleteRow,
    isDropZoneVisible,
    setIsDropZoneVisible,
  } = useLocationPage();

  return (
    <Box mx={"40px"} my={SCREEN_VERTICAL_SPACING}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Text fontSize="3xl" color="black">
            {t("locations")}
          </Text>
          <Text fontSize="md" color="#3A3A3A">
            {t("description")}
          </Text>
        </Box>

        <HStack justify="space-between" align="center">
          <LocationSearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
          <LocationCreateMenu onCreateLocation={handleCreateLocation} />
        </HStack>

        <EditStationModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          station={editStation ?? undefined}
          handleSaveEditStation={handleSaveEditStation}
          isLoading={isStationLoading}
        />

        {filteredLocations.length === 0 &&
        locationStore.tableState !== StoreState.PEDDING ? (
          <EmptyState
            description={
              searchTerm ? t("noLocationsFound") : t("noLocationsYet")
            }
            image={noLocationsImage}
            imageSize="200px"
            buttonText={
              !searchTerm ? (t("createLocation") as string) : undefined
            }
            buttonAction={
              !searchTerm
                ? () => handleCreateLocation(CoordinatesType.AREA)
                : undefined
            }
            marginTop="40px"
          />
        ) : (
          <DraggableTable
            data={filteredLocations}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
            showEditButton={true}
            showDeleteButton={true}
            disableDragAndDrop={true}
            enablePagination={enablePagination}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            isLoading={locationStore.tableState === StoreState.PEDDING}
            canDeleteRow={canDeleteRow}
            tableStyle={{
              color: "#71717A",
              backgroundColor: "#FAFAFA",
              marginTop: "10px",
            }}
            rowStyle={{
              backgroundColor: "white",
              borderColor: "rgba(30, 82, 151, 0.12)",
              color: "newBlue.500",
              height: "56px",
            }}
          />
        )}
      </VStack>

      <LocationDrawer
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isEditing={isEditing}
        isLoading={isLoading}
        handleDrawingMode={handleDrawingMode}
        handleGeoJsonLoaded={handleGeoJsonLoaded}
        clearGeofence={clearGeofence}
        selectedGeofence={selectedGeofence}
        setIsGeofenceLoadedByGeoJson={setIsGeofenceLoadedByGeoJson}
        showGeofenceError={
          locationStore.isGeofenceLoadedByGeoJson && isGeofenceInvalid
        }
        isGeofenceInvalid={isGeofenceInvalid}
        isDropZoneVisible={isDropZoneVisible}
        setIsDropZoneVisible={setIsDropZoneVisible}
      />

      <AlertDelete
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        titleLabel={t("deleteLocation")}
        bodyText={t("deleteConfirmation")}
        cancelButtonLabel={t("cancel")}
        deleteButtonLabel={t("delete")}
        handleDelete={handleConfirmDelete}
      />
    </Box>
  );
};

export default observer(LocationPage);
